import React, { useEffect, useState } from 'react';
import axiosInstance from '../lib/axiosInstance';

export default function EditProfile() {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [profile, setProfile] = useState({
        name: '',
        emailID: '',
        gstin: '',
        profilePhoto: null,
        username: '',
        shippingCharge: 0
    });

    const [otp, setOtp] = useState('');
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [otpSuccess, setOtpSuccess] = useState('');

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axiosInstance.get('/brand/profile');
            if (response.data?.success) {
                setProfile(response.data.data);
            } else {
                setError('Failed to load profile');
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfile(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
        setSuccess('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError('');
            setSuccess('');

            const response = await axiosInstance.put('/brand/profile', {
                name: profile.name,
                emailID: profile.emailID,
                gstin: profile.gstin,
                profilePhoto: profile.profilePhoto,
                shippingCharge: profile.shippingCharge
            });

            if (response.data?.success) {
                setSuccess('Profile updated successfully!');
                setProfile(response.data.data);
                // Update auth context if needed
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(response.data?.message || 'Failed to update profile');
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const handleSendEmailOtp = async () => {
        try {
            setOtpLoading(true);
            setOtpError('');
            setOtpSuccess('');
            const response = await axiosInstance.post('/brand/send-email-verification-otp');
            if (response.data?.success) {
                setShowOtpInput(true);
                setOtpSuccess('OTP sent to your email successfully!');
                setTimeout(() => setOtpSuccess(''), 3000);
            } else {
                setOtpError(response.data?.message || 'Failed to send OTP');
            }
        } catch (e) {
            setOtpError(e.response?.data?.message || 'Failed to send OTP');
        } finally {
            setOtpLoading(false);
        }
    };

    const handleVerifyEmailOtp = async () => {
        if (!otp) {
            setOtpError('Please enter the OTP');
            return;
        }

        try {
            setOtpLoading(true);
            setOtpError('');
            setOtpSuccess('');
            const response = await axiosInstance.post('/brand/verify-email-otp', { otp });
            if (response.data?.success) {
                setProfile(prev => ({ ...prev, verifiedEmail: 1 }));
                setShowOtpInput(false);
                setOtp('');
                setOtpSuccess('Email verified successfully!');
                setTimeout(() => setOtpSuccess(''), 3000);
            } else {
                setOtpError(response.data?.message || 'Failed to verify OTP');
            }
        } catch (e) {
            setOtpError(e.response?.data?.message || 'Failed to verify OTP');
        } finally {
            setOtpLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('Image size should be less than 5MB');
            return;
        }

        try {
            // Convert to base64
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfile(prev => ({
                    ...prev,
                    profilePhoto: reader.result
                }));
            };
            reader.readAsDataURL(file);
        } catch (err) {
            setError('Failed to process image');
        }
    };

    if (loading) {
        return (
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <div style={{ textAlign: 'center', padding: 40 }}>
                    <div style={{ fontSize: 16, color: 'var(--muted)' }}>Loading profile...</div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="lux-heading" style={{ fontSize: 28, fontWeight: 700, marginBottom: 24 }}>Edit Profile</h1>

            {error && (
                <div className="card p-4 mb-6" style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b' }}>
                    {error}
                </div>
            )}

            {success && (
                <div className="card p-4 mb-6" style={{ background: '#d1fae5', border: '1px solid #86efac', color: '#065f46' }}>
                    {success}
                </div>
            )}

            {otpError && (
                <div className="card p-4 mb-6" style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b' }}>
                    {otpError}
                </div>
            )}

            {otpSuccess && (
                <div className="card p-4 mb-6" style={{ background: '#d1fae5', border: '1px solid #86efac', color: '#065f46' }}>
                    {otpSuccess}
                </div>
            )}

            <form onSubmit={handleSubmit} className="card p-6">
                {/* Profile Photo */}
                <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
                        Profile Photo
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        {profile.profilePhoto ? (
                            <img
                                src={profile.profilePhoto}
                                alt="Profile"
                                style={{
                                    width: 100,
                                    height: 100,
                                    borderRadius: 8,
                                    objectFit: 'cover',
                                    border: '1px solid var(--border)'
                                }}
                            />
                        ) : (
                            <div
                                style={{
                                    width: 100,
                                    height: 100,
                                    borderRadius: 8,
                                    background: 'var(--surface)',
                                    border: '1px solid var(--border)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'var(--muted)',
                                    fontSize: 12
                                }}
                            >
                                No Image
                            </div>
                        )}
                        <div>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                style={{ display: 'none' }}
                                id="profile-photo-upload"
                            />
                            <label
                                htmlFor="profile-photo-upload"
                                style={{
                                    padding: '8px 16px',
                                    background: 'var(--primary)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    display: 'inline-block'
                                }}
                            >
                                Upload Photo
                            </label>
                            {profile.profilePhoto && (
                                <button
                                    type="button"
                                    onClick={() => setProfile(prev => ({ ...prev, profilePhoto: null }))}
                                    style={{
                                        marginLeft: 8,
                                        padding: '8px 16px',
                                        background: 'transparent',
                                        color: '#ef4444',
                                        border: '1px solid #ef4444',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                        fontSize: 14
                                    }}
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Username (Read-only) */}
                <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
                        Username
                    </label>
                    <input
                        type="text"
                        value={profile.username || ''}
                        disabled
                        style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            background: 'var(--surface)',
                            color: 'var(--muted)',
                            fontSize: 14
                        }}
                    />
                    <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Username cannot be changed</p>
                </div>

                {/* Name */}
                <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
                        Brand Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        value={profile.name || ''}
                        onChange={handleChange}
                        required
                        style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            background: 'var(--surface)',
                            color: 'var(--text)',
                            fontSize: 14
                        }}
                        placeholder="Enter brand name"
                    />
                </div>

                {/* Email */}
                <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
                        Email <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                        type="email"
                        name="emailID"
                        value={profile.emailID || ''}
                        onChange={handleChange}
                        required
                        style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            background: 'var(--surface)',
                            color: 'var(--text)',
                            fontSize: 14
                        }}
                        placeholder="Enter email address"
                    />
                </div>

                {/* GSTIN */}
                <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
                        GSTIN
                    </label>
                    <input
                        type="text"
                        name="gstin"
                        value={profile.gstin || ''}
                        onChange={handleChange}
                        maxLength={15}
                        style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            background: 'var(--surface)',
                            color: 'var(--text)',
                            fontSize: 14,
                            textTransform: 'uppercase'
                        }}
                        placeholder="Enter GSTIN (e.g., 36AAHCI0804Q1ZB)"
                    />
                    <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                        Format: 15 characters (e.g., 36AAHCI0804Q1ZB)
                    </p>
                </div>
                
                {/* Shipping Charge */}
                <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
                        Shipping Charge (INR)
                    </label>
                    <input
                        type="number"
                        name="shippingCharge"
                        value={profile.shippingCharge || 0}
                        onChange={handleChange}
                        style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            background: 'var(--surface)',
                            color: 'var(--text)',
                            fontSize: 14
                        }}
                        placeholder="Enter shipping charge for your products"
                    />
                    <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                        This charge will be applied once per order if the total order value is below ₹999.
                    </p>
                </div>

                {/* Account Info */}
                <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--text)' }}>Account Information</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 14, color: 'var(--muted)' }}>Account Created:</span>
                            <span style={{ fontSize: 14, color: 'var(--text)' }}>
                                {profile.createdOn ? new Date(profile.createdOn).toLocaleDateString('en-IN') : '-'}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 14, color: 'var(--muted)' }}>Email Verified:</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span style={{ fontSize: 14, color: profile.verifiedEmail ? '#10b981' : '#ef4444' }}>
                                    {profile.verifiedEmail ? 'Yes' : 'No'}
                                </span>
                                {!profile.verifiedEmail && (
                                    <button
                                        type="button"
                                        onClick={handleSendEmailOtp}
                                        disabled={otpLoading}
                                        style={{
                                            padding: '4px 12px',
                                            background: 'var(--primary)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: 4,
                                            cursor: otpLoading ? 'not-allowed' : 'pointer',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            opacity: otpLoading ? 0.7 : 1
                                        }}
                                    >
                                        {otpLoading ? 'Sending...' : 'Verify'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {showOtpInput && !profile.verifiedEmail && (
                            <div style={{ 
                                marginTop: 12, 
                                padding: 16, 
                                background: 'var(--surface)', 
                                border: '1px solid var(--border)', 
                                borderRadius: 8 
                            }}>
                                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
                                    Enter Verification OTP
                                </label>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <input
                                        type="text"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        placeholder="Enter OTP sent to your email"
                                        style={{
                                            flex: 1,
                                            padding: '10px 12px',
                                            border: '1px solid var(--border)',
                                            borderRadius: 6,
                                            background: 'var(--surface)',
                                            color: 'var(--text)',
                                            fontSize: 14
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleVerifyEmailOtp}
                                        disabled={otpLoading || !otp}
                                        style={{
                                            padding: '10px 20px',
                                            background: (otpLoading || !otp) ? '#9ca3af' : '#10b981',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: 6,
                                            cursor: (otpLoading || !otp) ? 'not-allowed' : 'pointer',
                                            fontSize: 14,
                                            fontWeight: 600
                                        }}
                                    >
                                        {otpLoading ? 'Verifying...' : 'Submit OTP'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Submit Button */}
                <div style={{ marginTop: 32, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <button
                        type="button"
                        onClick={loadProfile}
                        disabled={saving}
                        style={{
                            padding: '10px 20px',
                            background: 'transparent',
                            color: 'var(--text)',
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            cursor: saving ? 'not-allowed' : 'pointer',
                            fontSize: 14,
                            fontWeight: 600
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        style={{
                            padding: '10px 20px',
                            background: saving ? '#9ca3af' : 'var(--primary)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 6,
                            cursor: saving ? 'not-allowed' : 'pointer',
                            fontSize: 14,
                            fontWeight: 600
                        }}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
}

